// components/player.js
// Zero-dependency custom player using native iframes + <video>
// YouTube and Vimeo iframes handle their own controls natively.

export default function initPlayer() {
  const wrapper = document.querySelector('.player');
  const closeBtn = document.querySelector('.player__close');

  // data-youtube can hold YouTube OR Vimeo full URLs
  const embedBtns = Array.from(document.querySelectorAll('[data-youtube]'));
  const fileBtns = Array.from(document.querySelectorAll('[data-video]'));

  if (!embedBtns.length && !fileBtns.length) return;
  if (!wrapper) return;

  const container = wrapper.querySelector('#player');
  if (!container) return;

  // ─── URL helpers ──────────────────────────────────────────────────────────

  const getYoutubeId = (url = '') => {
    const patterns = [
      /(?:youtube\.com\/.*[?&]v=)([^&]+)/i,
      /youtu\.be\/([^?&#]+)/i,
      /youtube\.com\/shorts\/([^?&#]+)/i,
      /youtube\.com\/embed\/([^?&#]+)/i,
    ];
    for (const rx of patterns) {
      const m = url.match(rx);
      if (m && m[1]) return m[1];
    }
    return null;
  };

  const getVimeoId = (url = '') => {
    const m = url.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d+)/i);
    return m ? m[1] : null;
  };

  const guessMime = (src, fallback = 'video/mp4') => {
    const ext = (src.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase();
    if (ext === 'webm') return 'video/webm';
    if (ext === 'ogv' || ext === 'ogg') return 'video/ogg';
    return fallback;
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  const clearContainer = () => {
    container.innerHTML = '';
  };

  const renderIframe = (src) => {
    clearContainer();
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', src);
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.style.cssText = 'width:100%;height:100%;display:block;';
    container.appendChild(iframe);
  };

  const renderVideo = (src, type) => {
    clearContainer();
    const video = document.createElement('video');
    video.setAttribute('controls', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.style.cssText = 'width:100%;height:100%;display:block;';
    const source = document.createElement('source');
    source.setAttribute('src', src);
    source.setAttribute('type', type);
    video.appendChild(source);
    container.appendChild(video);
    video.play().catch(() => { });
  };

  // ─── Open ─────────────────────────────────────────────────────────────────

  let triggerButton = null;

  const openEmbed = (url) => {
    const ytId = getYoutubeId(url);
    if (ytId) {
      renderIframe(
        `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`
      );
      return;
    }
    const vimeoId = getVimeoId(url);
    if (vimeoId) {
      renderIframe(
        `https://player.vimeo.com/video/${vimeoId}?autoplay=1&dnt=1`
      );
      return;
    }
  };

  const openFile = (src, type) => {
    renderVideo(src, type || guessMime(src));
  };

  // ─── Show / Hide ──────────────────────────────────────────────────────────

  const show = () => {
    wrapper.classList.add('show');
    wrapper.setAttribute('aria-hidden', 'false');
  };

  const hide = () => {
    // Clearing the container stops video/audio playback immediately
    clearContainer();
    wrapper.classList.remove('show');
    wrapper.setAttribute('aria-hidden', 'true');
    if (triggerButton && typeof triggerButton.focus === 'function') {
      triggerButton.focus();
    }
    triggerButton = null;
  };

  // ─── Wire embed buttons (YouTube + Vimeo via data-youtube) ───────────────

  embedBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (btn.tagName === 'A') e.preventDefault();
      const url = btn.dataset.youtube || '';
      if (!url) return;
      triggerButton = btn;
      show();
      openEmbed(url);
    });
  });

  // ─── Wire local / file video buttons ─────────────────────────────────────

  fileBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (btn.tagName === 'A') e.preventDefault();
      const src = btn.dataset.video;
      if (!src) return;
      const type = btn.dataset.type || guessMime(src);
      triggerButton = btn;
      show();
      openFile(src, type);
    });
  });

  // ─── Close button ─────────────────────────────────────────────────────────

  if (closeBtn) {
    closeBtn.addEventListener('click', () => hide());
  }

  // ─── ESC key ──────────────────────────────────────────────────────────────

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && wrapper.classList.contains('show')) hide();
  });

  // ─── Click backdrop to close ──────────────────────────────────────────────

  wrapper.querySelector('.wrapper')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) hide();
  });
}
